import { LogManager } from "@mcleod/core";
import { Image } from "@mcleod/components";

const log = LogManager.getLogger("mobile-components.PinchZoomHandler");

/**
 * Handles pinch-to-zoom and pan gestures for an Image component on mobile devices.
 *
 * Features:
 * - Pinch to zoom in/out
 * - Double-tap to zoom in (or reset if already zoomed)
 * - Pan/drag when zoomed in
 * - Respects min/max zoom limits
 */
export class PinchZoomHandler {
    // Zoom constraints
    private readonly MIN_ZOOM = 1.0;  // Can't zoom out past original size
    private readonly MAX_ZOOM = 5.0;  // 500% max zoom
    private readonly DOUBLE_TAP_ZOOM = 2.5;  // Zoom level for double-tap
    private readonly DOUBLE_TAP_THRESHOLD_MS = 300;  // Max time between taps for double-tap

    // Current transform state
    private scale: number = 1.0;
    private translateX: number = 0;
    private translateY: number = 0;

    // Touch tracking for pinch
    private initialDistance: number = 0;
    private initialScale: number = 1.0;
    private initialMidpoint: { x: number; y: number } | null = null;

    // Touch tracking for pan
    private isPanning: boolean = false;
    private panStartX: number = 0;
    private panStartY: number = 0;
    private panStartTranslateX: number = 0;
    private panStartTranslateY: number = 0;

    // Double-tap detection
    private lastTapTime: number = 0;
    private lastTapX: number = 0;
    private lastTapY: number = 0;

    // References
    private image: Image;
    private container: HTMLElement;

    // Bound event handlers for cleanup
    private boundTouchStart: (e: TouchEvent) => void;
    private boundTouchMove: (e: TouchEvent) => void;
    private boundTouchEnd: (e: TouchEvent) => void;

    constructor(image: Image) {
        this.image = image;
        this.container = image._element.parentElement || image._element;

        // Bind handlers
        this.boundTouchStart = this.onTouchStart.bind(this);
        this.boundTouchMove = this.onTouchMove.bind(this);
        this.boundTouchEnd = this.onTouchEnd.bind(this);

        this.attach();
    }

    /**
     * Attaches touch event listeners to the image.
     */
    private attach(): void {
        const element = this.image._element;

        // Prevent default touch behaviors (like browser zoom)
        element.style.touchAction = 'none';

        element.addEventListener('touchstart', this.boundTouchStart, { passive: false });
        element.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        element.addEventListener('touchend', this.boundTouchEnd, { passive: false });

        log.debug("PinchZoomHandler attached to image");
    }

    /**
     * Detaches touch event listeners from the image.
     */
    public detach(): void {
        const element = this.image._element;

        element.removeEventListener('touchstart', this.boundTouchStart);
        element.removeEventListener('touchmove', this.boundTouchMove);
        element.removeEventListener('touchend', this.boundTouchEnd);

        // Reset transform
        this.resetZoom();

        log.debug("PinchZoomHandler detached from image");
    }

    /**
     * Handles touch start event.
     */
    private onTouchStart(event: TouchEvent): void {
        if (event.touches.length === 2) {
            // Pinch gesture starting
            event.preventDefault();
            this.isPanning = false;

            const touch1 = event.touches[0];
            const touch2 = event.touches[1];

            this.initialDistance = this.getDistance(touch1, touch2);
            this.initialScale = this.scale;
            this.initialMidpoint = this.getMidpoint(touch1, touch2);

            log.debug(`Pinch start: distance=${this.initialDistance}, scale=${this.initialScale}`);

        } else if (event.touches.length === 1) {
            // Check for double-tap
            const touch = event.touches[0];
            const now = Date.now();
            const timeSinceLastTap = now - this.lastTapTime;
            const distanceFromLastTap = Math.sqrt(
                Math.pow(touch.clientX - this.lastTapX, 2) +
                Math.pow(touch.clientY - this.lastTapY, 2)
            );

            if (timeSinceLastTap < this.DOUBLE_TAP_THRESHOLD_MS && distanceFromLastTap < 50) {
                // Double-tap detected
                event.preventDefault();
                this.handleDoubleTap(touch.clientX, touch.clientY);
                this.lastTapTime = 0; // Reset to prevent triple-tap
                return;
            }

            this.lastTapTime = now;
            this.lastTapX = touch.clientX;
            this.lastTapY = touch.clientY;

            // Start pan gesture (only if zoomed in)
            if (this.scale > 1.0) {
                event.preventDefault();
                this.isPanning = true;
                this.panStartX = touch.clientX;
                this.panStartY = touch.clientY;
                this.panStartTranslateX = this.translateX;
                this.panStartTranslateY = this.translateY;
            }
        }
    }

    /**
     * Handles touch move event.
     */
    private onTouchMove(event: TouchEvent): void {
        if (event.touches.length === 2 && this.initialDistance > 0) {
            // Pinch gesture
            event.preventDefault();

            const touch1 = event.touches[0];
            const touch2 = event.touches[1];

            const currentDistance = this.getDistance(touch1, touch2);
            const currentMidpoint = this.getMidpoint(touch1, touch2);

            // Calculate new scale
            const scaleChange = currentDistance / this.initialDistance;
            let newScale = this.initialScale * scaleChange;
            newScale = this.constrainScale(newScale);

            // Calculate translation to zoom toward pinch center
            if (this.initialMidpoint && newScale !== this.scale) {
                const containerRect = this.container.getBoundingClientRect();
                const midX = currentMidpoint.x - containerRect.left;
                const midY = currentMidpoint.y - containerRect.top;

                // Adjust translation to keep the pinch center stable
                const scaleRatio = newScale / this.scale;
                this.translateX = midX - (midX - this.translateX) * scaleRatio;
                this.translateY = midY - (midY - this.translateY) * scaleRatio;
            }

            this.scale = newScale;
            this.constrainTranslation();
            this.applyTransform();

        } else if (event.touches.length === 1 && this.isPanning) {
            // Pan gesture
            event.preventDefault();

            const touch = event.touches[0];
            const deltaX = touch.clientX - this.panStartX;
            const deltaY = touch.clientY - this.panStartY;

            this.translateX = this.panStartTranslateX + deltaX;
            this.translateY = this.panStartTranslateY + deltaY;

            this.constrainTranslation();
            this.applyTransform();
        }
    }

    /**
     * Handles touch end event.
     */
    private onTouchEnd(event: TouchEvent): void {
        if (event.touches.length < 2) {
            // Pinch gesture ended
            this.initialDistance = 0;
            this.initialMidpoint = null;

            // If scale is very close to 1, snap to 1 and reset translation
            if (this.scale < 1.05) {
                this.resetZoom();
            }
        }

        if (event.touches.length === 0) {
            this.isPanning = false;
        }

        // If one finger remains after pinch, start pan mode
        if (event.touches.length === 1 && this.scale > 1.0) {
            const touch = event.touches[0];
            this.isPanning = true;
            this.panStartX = touch.clientX;
            this.panStartY = touch.clientY;
            this.panStartTranslateX = this.translateX;
            this.panStartTranslateY = this.translateY;
        }
    }

    /**
     * Handles double-tap to zoom in/out.
     */
    private handleDoubleTap(clientX: number, clientY: number): void {
        const containerRect = this.container.getBoundingClientRect();
        const tapX = clientX - containerRect.left;
        const tapY = clientY - containerRect.top;

        if (this.scale > 1.05) {
            // Already zoomed in, reset to normal
            log.debug("Double-tap: resetting zoom");
            this.resetZoom();
        } else {
            // Zoom in to the tap point
            log.debug(`Double-tap: zooming to ${this.DOUBLE_TAP_ZOOM}x at (${tapX}, ${tapY})`);

            const newScale = this.DOUBLE_TAP_ZOOM;
            const scaleRatio = newScale / this.scale;

            // Calculate translation to center the tap point
            this.translateX = tapX - (tapX - this.translateX) * scaleRatio;
            this.translateY = tapY - (tapY - this.translateY) * scaleRatio;
            this.scale = newScale;

            this.constrainTranslation();
            this.applyTransformAnimated();
        }
    }

    /**
     * Resets zoom to original state.
     */
    public resetZoom(): void {
        this.scale = 1.0;
        this.translateX = 0;
        this.translateY = 0;
        this.applyTransformAnimated();
    }

    /**
     * Applies the current transform to the image element.
     */
    private applyTransform(): void {
        this.image._element.style.transformOrigin = 'top left';
        this.image._element.style.transform =
            `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
    }

    /**
     * Applies the current transform with a smooth animation.
     */
    private applyTransformAnimated(): void {
        this.image._element.style.transition = 'transform 0.2s ease-out';
        this.applyTransform();

        // Remove transition after animation completes
        setTimeout(() => {
            this.image._element.style.transition = '';
        }, 200);
    }

    /**
     * Constrains the scale within min/max bounds.
     */
    private constrainScale(scale: number): number {
        return Math.min(Math.max(scale, this.MIN_ZOOM), this.MAX_ZOOM);
    }

    /**
     * Constrains translation to prevent panning outside image bounds.
     */
    private constrainTranslation(): void {
        // Only constrain if zoomed in
        if (this.scale <= 1.0) {
            this.translateX = 0;
            this.translateY = 0;
            return;
        }

        const containerRect = this.container.getBoundingClientRect();
        const scaledWidth = containerRect.width * this.scale;
        const scaledHeight = containerRect.height * this.scale;

        // Calculate max translation bounds
        const maxTranslateX = 0;
        const minTranslateX = containerRect.width - scaledWidth;
        const maxTranslateY = 0;
        const minTranslateY = containerRect.height - scaledHeight;

        // Constrain X
        if (scaledWidth > containerRect.width) {
            this.translateX = Math.min(maxTranslateX, Math.max(minTranslateX, this.translateX));
        } else {
            // Center if image is smaller than container
            this.translateX = (containerRect.width - scaledWidth) / 2;
        }

        // Constrain Y
        if (scaledHeight > containerRect.height) {
            this.translateY = Math.min(maxTranslateY, Math.max(minTranslateY, this.translateY));
        } else {
            // Center if image is smaller than container
            this.translateY = (containerRect.height - scaledHeight) / 2;
        }
    }

    /**
     * Calculates the distance between two touch points.
     */
    private getDistance(touch1: Touch, touch2: Touch): number {
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Calculates the midpoint between two touch points.
     */
    private getMidpoint(touch1: Touch, touch2: Touch): { x: number; y: number } {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    }
}

